import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { websocketService } from '../services/websocketService';
import { useWebSocketMonitor } from '../hooks/useWebSocketMonitor';
import StatsCard from '../components/StatsCard';
import ConnectionTable from '../components/ConnectionTable';
// import ConnectionStatusIndicator from '../components/ConnectionStatusIndicator';
import type { WebSocketMessage } from '../types/api';

const WebSocketMonitorPage: React.FC = () => {
  const { hasScope } = useAuth();
  const isDeveloper = hasScope('dev');

  const {
    stats,
    connections,
    profileConnections,
    isLoading,
    error,
    lastUpdate,
    loadData,
    disconnectConnection,
    broadcastMessage,
    clearError,
    autoRefresh,
    setAutoRefresh,
  } = useWebSocketMonitor();

  const [selectedTab, setSelectedTab] = useState<'overview' | 'connections' | 'profiles'>('overview');

  // Redirect non-dev users
  if (!isDeveloper) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600">This page is only available to developers.</p>
        </div>
      </div>
    );
  }

  const handleBroadcastMessage = async () => {
    const message: WebSocketMessage = {
      type: 'system_notification',
      data: {
        title: 'System Maintenance',
        message: 'The system will undergo maintenance in 10 minutes.',
        severity: 'warning'
      },
      timestamp: new Date().toISOString()
    };

    try {
      await broadcastMessage(message);
      alert('Message broadcasted successfully');
    } catch (error: any) {
      // Error is already handled by the hook
    }
  };

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat().format(num);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                WebSocket Monitor
              </h1>
              <p className="text-gray-600 text-sm sm:text-base">
                WebSocket connection monitoring via FPX server HTTP API
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Auto-refresh Toggle */}
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`px-3 py-1 text-xs rounded-md font-medium ${
                  autoRefresh 
                    ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {autoRefresh ? '⏸️ Auto-refresh' : '▶️ Manual'}
              </button>

              {/* Manual Refresh */}
              <button
                onClick={loadData}
                disabled={isLoading}
                className="px-3 py-1 text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-md font-medium disabled:opacity-50"
              >
                {isLoading ? '⏳' : '🔄'} Refresh
              </button>

              {/* Broadcast Message */}
              <button
                onClick={handleBroadcastMessage}
                className="px-3 py-1 text-xs bg-orange-100 text-orange-700 hover:bg-orange-200 rounded-md font-medium"
              >
                📢 Broadcast
              </button>
            </div>
          </div>

          {/* Last Update */}
          {lastUpdate && (
            <div className="text-xs text-gray-500 mt-2">
              Last updated: {lastUpdate.toLocaleTimeString()}
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <span className="text-red-400">⚠️</span>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                  <button
                    onClick={clearError}
                    className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', name: 'Overview', icon: '📊' },
              { id: 'connections', name: 'Active Connections', icon: '🔌' },
              { id: 'profiles', name: 'Profile Status', icon: '👤' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setSelectedTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  selectedTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.icon} {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {selectedTab === 'overview' && stats && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatsCard
                title="Active Connections"
                value={stats.activeConnections}
                icon="🔌"
                color="blue"
              />

              <StatsCard
                title="Connected Users"
                value={stats.totalUsers}
                icon="👥"
                color="green"
              />

              <StatsCard
                title="Connected Profiles"
                value={stats.connectionsWithProfiles}
                icon="👤"
                color="blue"
                subtitle={`${stats.connectionsWithoutProfiles} without profiles`}
              />

              <StatsCard
                title="Messages/sec"
                value={stats.messageStats.messagesPerSecond.toFixed(1)}
                icon="💬"
                color="gray"
              />
            </div>

            {/* Additional Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Connection Status</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">🟢 Connected</span>
                    <span className="text-sm font-medium">{formatNumber(stats.connectionsByStatus.connected)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">🟡 Reconnecting</span>
                    <span className="text-sm font-medium">{formatNumber(stats.connectionsByStatus.reconnecting)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">🔴 Disconnected</span>
                    <span className="text-sm font-medium">{formatNumber(stats.connectionsByStatus.disconnected)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Message Statistics</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Total Sent</span>
                    <span className="text-sm font-medium">{formatNumber(stats.messageStats.totalSent)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Total Received</span>
                    <span className="text-sm font-medium">{formatNumber(stats.messageStats.totalReceived)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Average Connection Time</span>
                    <span className="text-sm font-medium">{websocketService.formatConnectionDuration(new Date(Date.now() - stats.averageConnectionTime * 1000).toISOString())}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Active Connections Tab */}
        {selectedTab === 'connections' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Active WebSocket Connections ({connections.length})
              </h3>

              <ConnectionTable
                connections={connections}
                onDisconnect={disconnectConnection}
                isLoading={isLoading}
              />
            </div>
          </div>
        )}

        {/* Profile Status Tab */}
        {selectedTab === 'profiles' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Profile Connection Status ({profileConnections.length})
              </h3>

              {profileConnections.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p>No profiles found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Profile
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Connection Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Last Seen
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Messages
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {profileConnections.map((profile) => (
                        <tr key={profile.profileId}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <span className="text-lg mr-2">
                                {profile.isConnected ? '🟢' : '🔴'}
                              </span>
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {profile.profileName}
                                </div>
                                <div className="text-sm text-gray-500">
                                  ID: {profile.profileId.substring(0, 8)}...
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {profile.userName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {profile.userId.substring(0, 8)}...
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              profile.isConnected
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {profile.isConnected ? 'Connected' : 'Disconnected'}
                            </span>
                            {profile.connectionId && (
                              <div className="text-xs text-gray-500 mt-1">
                                Connection: {profile.connectionId.substring(0, 8)}...
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {profile.lastSeen
                              ? websocketService.formatLastActivity(profile.lastSeen)
                              : 'Never'
                            }
                            {profile.connectionTime && profile.isConnected && (
                              <div className="text-xs text-gray-500">
                                Connected: {websocketService.formatConnectionDuration(profile.connectionTime)}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {profile.messageCount ? (
                              <div>
                                <div className="text-sm text-gray-900">
                                  ↑ {formatNumber(profile.messageCount.sent)}
                                </div>
                                <div className="text-sm text-gray-500">
                                  ↓ {formatNumber(profile.messageCount.received)}
                                </div>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-500">No data</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Summary */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <span className="text-green-600 text-lg mr-2">🟢</span>
                    <div>
                      <p className="text-sm font-medium text-green-800">Connected Profiles</p>
                      <p className="text-lg font-semibold text-green-900">
                        {profileConnections.filter(p => p.isConnected).length}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-red-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <span className="text-red-600 text-lg mr-2">🔴</span>
                    <div>
                      <p className="text-sm font-medium text-red-800">Disconnected Profiles</p>
                      <p className="text-lg font-semibold text-red-900">
                        {profileConnections.filter(p => !p.isConnected).length}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <span className="text-blue-600 text-lg mr-2">📊</span>
                    <div>
                      <p className="text-sm font-medium text-blue-800">Connection Rate</p>
                      <p className="text-lg font-semibold text-blue-900">
                        {profileConnections.length > 0
                          ? Math.round((profileConnections.filter(p => p.isConnected).length / profileConnections.length) * 100)
                          : 0
                        }%
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WebSocketMonitorPage;
